import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import '../css/home-popup.css';

// Homepage popups, shown once per browser session between start and end (Guadeloupe time).
// To add a new one: put the image in public/assets/popups/ and add an entry below.
// The first entry whose dates include "now" is shown.
const POPUPS = [
    {
        id: 'ichali_route_du_rhum_2026',
        start: '2026-11-03T00:00:00-04:00',
        end: '2026-11-15T23:59:59-04:00', // last day of the Route du Rhum offer
        image: '/assets/popups/ichali-route-du-rhum-2026.jpg',
        alt: 'MACTe Route du Rhum – Ichali Private Experience',
        link: '/restaurant-ichali',
        cta: 'Découvrir Ichali',
        theme: {
            background: 'linear-gradient(180deg, #1c1f5c 0%, #14164a 100%)',
            ctaBackground: '#f2c94c',
            ctaHover: '#f6e25a',
            ctaColor: '#14164a',
        },
    },
    {
        id: 'papa_yaya_2026',
        start: '2026-10-16T00:00:00-04:00',
        end: '2026-10-25T16:00:00-04:00', // start of the last show
        image: '/assets/popups/papa-yaya-2026.jpg',
        alt: "L'extraordinaire et véritable histoire de Papa Yaya – théâtre, les 24 et 25 octobre 2026 à l'auditorium Jacques-Martial du MACTe",
        link: '/evenement/dtmjf5k0y7j15ezg3kd6lyxt',
        cta: 'Découvrir le spectacle',
        theme: {
            background: 'linear-gradient(180deg, #6b6b67 0%, #2b2b2a 100%)',
            ctaBackground: '#c8102e',
            ctaHover: '#a00c24',
        },
    },
    {
        id: 'monde_en_vues_2026',
        start: '2026-10-12T00:00:00-04:00',
        end: '2026-10-14T23:59:59-04:00',
        image: '/assets/popups/monde-en-vues-2026.jpg',
        alt: 'Monde en Vues, 13e édition – Archipels du Nous – du 9 au 14 octobre 2026 au MACTe',
        link: '/evenement/qboyp3vflf4nqukq1xzn2aj4',
        cta: 'Voir le programme au MACTe',
        theme: {
            background: 'linear-gradient(180deg, #e2b230 0%, #e0601f 100%)',
            ctaBackground: '#1f3a3a',
            ctaHover: '#142828',
        },
    },
];

const HOME_PATHS = ['/', '/accueil'];

const getCurrentPopup = () => {
    const now = new Date();
    return POPUPS.find(p => new Date(p.start) <= now && now <= new Date(p.end)) || null;
};

const storageKey = (popup) => `popup_${popup.id}`;

const alreadySeen = (popup) => {
    try {
        return !!sessionStorage.getItem(storageKey(popup));
    } catch {
        return false;
    }
};

const markSeen = (popup) => {
    try {
        sessionStorage.setItem(storageKey(popup), '1');
    } catch {
        // Storage unavailable (private mode): the popup may show again, that's fine
    }
};

const HomePopup = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const [popup, setPopup] = useState(null);
    const [closing, setClosing] = useState(false);
    const closeButtonRef = useRef(null);
    const isHome = HOME_PATHS.includes(location.pathname);

    // Open once per browser session, on the homepage, while a popup is scheduled
    useEffect(() => {
        if (!isHome) return;
        const current = getCurrentPopup();
        if (!current || alreadySeen(current)) return;
        const timer = setTimeout(() => {
            setPopup(current);
            markSeen(current);
        }, 600);
        return () => clearTimeout(timer);
    }, [isHome]);

    const close = useCallback(() => {
        setClosing(true);
        setTimeout(() => {
            setPopup(null);
            setClosing(false);
        }, 250);
    }, []);

    const goToLink = () => {
        setPopup(null);
        if (/^https?:\/\//.test(popup.link)) {
            window.open(popup.link, '_blank', 'noopener,noreferrer');
        } else {
            navigate(popup.link);
        }
    };

    // While open: lock page scroll, close on Escape, focus the close button
    useEffect(() => {
        if (!popup) return;
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        closeButtonRef.current?.focus();

        const onKeyDown = (e) => {
            if (e.key === 'Escape') close();
        };
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = previousOverflow;
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [popup, close]);

    if (!popup) return null;

    const themeStyle = {
        '--home-popup-bg': popup.theme?.background,
        '--home-popup-cta-bg': popup.theme?.ctaBackground,
        '--home-popup-cta-hover': popup.theme?.ctaHover,
        '--home-popup-cta-color': popup.theme?.ctaColor,
    };

    return (
        <div className={`home-popup ${closing ? 'is-closing' : ''}`} onClick={close}>
            <div
                className="home-popup__dialog"
                style={themeStyle}
                role="dialog"
                aria-modal="true"
                aria-label={popup.alt}
                onClick={(e) => e.stopPropagation()}
            >
                <button
                    ref={closeButtonRef}
                    type="button"
                    className="home-popup__close"
                    onClick={close}
                    aria-label="Fermer"
                >
                    <span aria-hidden="true">×</span>
                </button>

                {popup.link ? (
                    <button type="button" className="home-popup__poster" onClick={goToLink}>
                        <img src={popup.image} alt={popup.alt} />
                    </button>
                ) : (
                    <div className="home-popup__poster">
                        <img src={popup.image} alt={popup.alt} />
                    </div>
                )}

                {popup.link && popup.cta && (
                    <div className="home-popup__footer">
                        <button type="button" className="home-popup__cta" onClick={goToLink}>
                            {popup.cta}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default HomePopup;
